import { useCallback, useEffect, useMemo, useState } from 'react';
import { IMicrofrontendManifest } from '@cian/mf-registry/shared';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { IApplicationContext } from 'shared/types/applicationContext';
import type { IUIRuntime } from '@cian/mf-registry/browser/runtime';

export enum EStatus {
  Initial = 'initial',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

type TFetchManifest<T> = (
  context: IApplicationContext,
  parameters?: TParameters<T>,
) => Promise<IMicrofrontendManifest | null>;

type TParameters<T> = {
  parameters?: T;
};

export interface IUseMicrofrontendParams<T> {
  fetchFunction: TFetchManifest<T>;
  parameters?: T;
  fetchOnMount?: boolean;
}

export const useMicrofrontend = <T>({ fetchFunction, parameters, fetchOnMount }: IUseMicrofrontendParams<T>) => {
  const context = useApplicationContext();

  const [manifest, setManifest] = useState<IMicrofrontendManifest | null>(null);
  const [status, setStatus] = useState<EStatus>(EStatus.Initial);

  const fetchManifest = useCallback(async () => {
    setStatus(EStatus.Loading);

    const manifest = await fetchFunction(context, { parameters });

    if (manifest) {
      const image = context.microfrontendsRegistry.register(manifest);

      const runtime = image.runtimeStrict<IUIRuntime>(manifest.runtimeName);

      await runtime.ensure();

      setManifest(manifest);

      setStatus(EStatus.Success);
    } else {
      setStatus(EStatus.Error);
    }
  }, [context, fetchFunction, parameters]);

  useEffect(() => {
    if (fetchOnMount) {
      fetchManifest();
    }
  }, []); //eslint-disable-line

  return useMemo(() => ({ manifest, fetchManifest, status }), [fetchManifest, manifest, status]);
};
